/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ClockIcon,
  EmojiSadIcon,
  XIcon,
} from '@heroicons/react/outline';
import router from 'next/router';

import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import 'firebase/analytics';

import ConnectTwitter from '../Modal/connect-twitter';
import ReconnectTwitter from '../Modal/reconnect-twitter';
import EssayCard from '../Cards/essayCard';
import ThreadCard from '../Cards/thread';
import EmptyStateSection from '../EmptyState';
import ThreadIconDetailed from '../Icons/thread-icon-detailed';
import HorizontalTabs from '../Tabs/HorizontalTabs';
import Button from '../Button';
import IconButton from '../Button/IconButton';
import TwitterBirdIcon from '../Icons/twitter';
import EssayLoadingState from '../LoadingState/EssayLoadingState';
import { getTwitterID } from '../../utils/twitter';
import {
  FilterIcon,
  SearchIcon,
  filterAscendingIcon,
  SortAscendingIcon,
} from '@heroicons/react/solid';
import SpinnerIcon from '../Icons/spinner';
import toast, { Toaster } from 'react-hot-toast';
import QueueDraftCard from '../Cards/QueueDraftCard';
import { getDrafts } from '../../utils/library';

export default function QueueDraftSelector({
  open,
  setOpen,
  userID,
  activeSlotPretty,
  activeSlotUnix,
  draftSelected,
  refreshDrafts,
  setRefreshDrafts,
}) {
  const cancelButtonRef = useRef();
  const [searchTerms, setSearchTerms] = useState([]);
  const [search, setSearch] = useState('');
  const [fetching, setFetching] = useState(true);

  const [authToken, setAuthToken] = useState(null);

  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    userID && generateAuthToken();
  }, [userID]);

  const generateAuthToken = () => {
    firebase
      .auth()
      .currentUser.getIdToken(/* forceRefresh */ true)
      .then(function (idToken) {
        // Send token to your backend via HTTPS
        setAuthToken(idToken);
      })
      .catch(function (error) {
        //console.log(error)
        // Handle error
      });
  };

  useEffect(() => {
    authToken && drafts.length === 0 ? fetchDrafts() : null;
  }, [authToken, drafts]);

  useEffect(() => {
    if (refreshDrafts && authToken) {
      fetchDrafts();
    }
  }, [refreshDrafts]);

  async function fetchDrafts() {
    setRefreshDrafts(false);
    const draftsData = await getDrafts(authToken, { id: userID });
    setFetching(false);
    draftsData.length > 0 && setDrafts(draftsData);
  }

  //whenever search changes, filter the threads based on the search
  const searchFor = () => {
    search.length > 0 ? setSearchTerms([...searchTerms, search]) : null;
  };

  /* const searchTweets = (tweets) => {

        if (searchTerms.length > 0) {
            //filter imported threads
            //find the threads that match the search and return them

            const newImportedThreads = {}
            Object.keys(tweets).forEach(thread2 => {
                if (tweets[thread2]) {
                    //check all searchTerms against the thread. must match all searchTerms if searchAll is true
                    var thread = tweets[thread2].tweetArray[0]
                    var matchIndexs = [];

                    searchTerms.forEach((searchTerm, index) => {

                        if (thread.toLowerCase().includes(searchTerm.toLowerCase())) {
                            matchIndexs.push(index)
                        }

                    })

                    if (searchAll) {
                        if (matchIndexs.length === searchTerms.length) {
                            newImportedThreads[thread2] = tweets[thread2]
                        } else {

                        }
                    } else {

                        if (matchIndexs.length > 0) {
                            newImportedThreads[thread2] = tweets[thread2]
                        }

                    }
                } else {
                    setSearch('')
                    return newImportedThreads
                }
            })

            setSearch('')
            return newImportedThreads


        } else {

            setSearch('')
            return tweets

        }

    } */

  const removeFromSearchTerms = (i) => {
    //remove index i from searchTerms
    const newSearchTerms = [...searchTerms];
    newSearchTerms.splice(i, 1);
    setSearchTerms(newSearchTerms);
  };

  const successToast = (text) =>
    toast.success(text, {
      id: 'success',
      /* icon: '🪄', */
      style: {
        borderRadius: '100px',
        background: '#0F172A',
        color: '#fff',
      },
    });

  const problemToast = (text) =>
    toast.error(text, {
      id: 'error',
      /* icon: '🪄', */
      style: {
        borderRadius: '100px',
        background: '#0F172A',
        color: '#fff',
      },
    });

  return (
    <>
      <Transition.Root initialFocus={cancelButtonRef} show={open} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-40 antialiased"
          open={open}
          onClose={setOpen}
        >
          <div className="flex h-screen min-h-screen items-center justify-center overflow-auto pt-8 text-center antialiased sm:block sm:p-0 sm:px-4 sm:pt-4 sm:pb-20 ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-50" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:h-full sm:align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-24 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-24 sm:scale-95"
            >
              <div className="relative inline-block align-bottom antialiased transition-all sm:w-full sm:max-w-3xl sm:align-middle">
                <div
                  className={`relative inline-block w-full transform rounded-2xl bg-gray-50 pb-8 text-left align-middle shadow-xl transition-all sm:align-middle`}
                >
                  <div className="sticky top-1 z-10 w-full">
                    <button
                      onClick={() => setOpen(false)}
                      className="absolute top-2 right-2 rounded-full bg-gray-200 p-2 text-gray-500 duration-200 hover:bg-gray-300 hover:text-gray-600 focus:outline-none"
                    >
                      <XIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div
                    className={
                      'flex flex-col items-start justify-start px-8 pt-6'
                    }
                  >
                    <div className="relative mb-4 flex w-full flex-col">
                      <Dialog.Title
                        as="h3"
                        className="text-left font-sans text-xl font-semibold tracking-tight text-gray-900"
                      >
                        Choose a Draft to Schedule
                      </Dialog.Title>
                      <p className="text-sm text-gray-600">
                        You'll be able to preview and make edits before
                        scheduling.
                      </p>
                    </div>

                    {fetching ? (
                      <div className="border-gray-120 flex w-full flex-col items-center justify-center space-y-4 rounded-2xl border bg-gray-100 p-10">
                        <div className="flex max-w-sm flex-col items-center justify-center space-y-2 text-center">
                          <SpinnerIcon
                            className={'h-6 w-6 animate-spin text-gray-700'}
                          />
                          <p className="text-lg font-medium text-gray-900">
                            Finding your drafts...
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 flex max-h-[500px] w-full  flex-col items-start justify-start overflow-auto rounded-lg bg-gray-100 p-3">
                          {/* 
                                                <div className='w-full flex flex-row justify-start space-x-2 items-center mt-4'>
                                                    <div className=" relative w-full group">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <SearchIcon className="h-5 w-5 text-gray-400 group-focus-within:text-gray-700" aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            onChange={(e) => setSearch(e.target.value)}
                                                            value={search}
                                                            type="text"
                                                            name="search"
                                                            id="search"
                                                            className="focus:ring-gray-300 font-thread bg-gray-50 focus:border-gray-300 block pl-10 sm:text-sm border-gray-200 rounded-xl w-full"
                                                            placeholder="Search..."
                                                        />
                                                    </div>

                                                    <Button click={() => searchFor()} variant={"secondary"} disabled={search.length === 0} size={"mini"}>Search</Button>
                                                </div>

                                                {searchTerms.length > 0 &&

                                                    <div className=' mt-4 flex flex-row space-x-2 flex-wrap items-center'>

                                                        {searchTerms.length > 1 && <div className='text-sm font-medium text-gray-700'>Match <button onClick={() => setSearchAll(!searchAll)} className='px-2 py-0.5 bg-blue-500 bg-opacity-10 hover:bg-opacity-20 duration-200 text-blue-500 font-semibold rounded-md'>{searchAll ? "all" : "one"}</button> of:</div>}


                                                        {searchTerms.map((term, i) => (
                                                            <div className=' flex flex-row space-x-1 items-center justify-center bg-gray-50 text-gray-600 border-gray-100 px-2 py-1 rounded-lg text-sm font-medium border'>
                                                                <p>{term}</p>
                                                                <button onClick={() => removeFromSearchTerms(i)}>
                                                                    <XIcon className='w-3 h-3' />
                                                                </button>
                                                            </div>

                                                        ))}

                                                    </div>}

                                                <p className=' mt-4 text-gray-500 font-sans font-medium text-sm'>Showing {drafts.length} {drafts.length === 1 ? "result" : "results"}</p>

 */}

                          {drafts.length > 0 ? (
                            <div
                              className={
                                'grid w-full grid-cols-1 gap-2 sm:grid-cols-2'
                              }
                            >
                              {drafts.map((draft, i) => (
                                <QueueDraftCard
                                  draft={draft}
                                  key={i}
                                  draftSelected={(id) => draftSelected(id)}
                                />
                              ))}
                            </div>
                          ) : (
                            <div className="mt-8 flex w-full flex-col items-center justify-center space-y-1 font-sans text-lg font-medium">
                              <p className="text-2xl">😢</p>
                              <p>No results...</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    <div className="mt-2 flex w-full items-center justify-center text-sm font-medium text-gray-600">
                      <p>
                        Click a draft to schedule for:{' '}
                        <span className="text-orange-500">
                          {activeSlotPretty}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
            <Toaster position="bottom-right" reverseOrder={false} />
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
